import type { TFunction } from 'i18next';
import type { CognitiveFitness } from '../../model';
import type { AssessmentResultItemDataFragment } from '@/gql/graphql';

export const buildDefaultCognitivePlaceholder = (
    t: TFunction,
    item: AssessmentResultItemDataFragment
): CognitiveFitness | null => {
    const mi = item.moduleInstance;
    if (!mi?.moduleDefinitionIdentifier) {
        return null;
    }

    return {
        name: mi.name ?? '',
        moduleDefinitionIdentifier: mi.moduleDefinitionIdentifier ?? '',
        description: mi.description ?? '',
        result: [
            {
                label: t('traineeReports:performance.cognitiveFitness.scoreLabel'),
                value: t('traineeReports:na'),
            },
            {
                label: t('traineeReports:performance.cognitiveFitness.avgResponseTimeMs'),
                value: t('traineeReports:na'),
            },
        ],
    };
};

export const buildDefaultNBackPlaceholder = (
    t: TFunction,
    item: AssessmentResultItemDataFragment
): CognitiveFitness | null => {
    const mi = item.moduleInstance;
    if (!mi?.moduleDefinitionIdentifier) {
        return null;
    }

    return {
        name: mi.name ?? '',
        moduleDefinitionIdentifier: mi.moduleDefinitionIdentifier ?? '',
        description: mi.description ?? '',
        result: [
            {
                label: t('traineeReports:performance.cognitiveFitness.scoreLabel'),
                value: t('traineeReports:na'),
            },
            {
                label: t('traineeReports:performance.cognitiveFitness.avgResponseTimeMs'),
                value: t('traineeReports:na'),
            },
            {
                label: t('traineeReports:performance.cognitiveFitness.dPrime'),
                value: t('traineeReports:na'),
            },
        ],
    };
};

export const buildDigitSpanPlaceholder = (
    t: TFunction,
    item: AssessmentResultItemDataFragment
): CognitiveFitness | null => {
    const mi = item.moduleInstance;
    if (!mi?.moduleDefinitionIdentifier) {
        return null;
    }

    return {
        moduleDefinitionIdentifier: mi.moduleDefinitionIdentifier,
        name: mi.name ?? '',
        description: mi.description ?? '',
        result: [
            {
                label: t('traineeReports:performance.cognitiveFitness.maxForwardSpanScore'),
                value: t('traineeReports:na'),
            },
            {
                label: t('traineeReports:performance.cognitiveFitness.forwardCorrectTrials'),
                value: t('traineeReports:na'),
            },
            {
                label: t('traineeReports:performance.cognitiveFitness.maxBackwardSpanScore'),
                value: t('traineeReports:na'),
            },
            {
                label: t('traineeReports:performance.cognitiveFitness.backwardCorrectTrials'),
                value: t('traineeReports:na'),
            },
        ],
    };
};

export const buildAitPlaceholder = (t: TFunction, item: AssessmentResultItemDataFragment): CognitiveFitness | null => {
    const mi = item.moduleInstance;
    if (!mi?.moduleDefinitionIdentifier) {
        return null;
    }

    return {
        moduleDefinitionIdentifier: mi.moduleDefinitionIdentifier,
        name: mi.name ?? '',
        description: mi.description ?? '',
        result: [
            {
                label: t('traineeReports:performance.cognitiveFitness.score'),
                value: t('traineeReports:na'),
            },
            {
                label: t('traineeReports:performance.cognitiveFitness.accuracy'),
                value: t('traineeReports:na'),
            },
            {
                label: t('traineeReports:performance.cognitiveFitness.flexibility'),
                value: t('traineeReports:na'),
            },
            {
                label: t('traineeReports:performance.cognitiveFitness.adaptiveSpeed'),
                value: t('traineeReports:na'),
            },
            {
                label: t('traineeReports:performance.cognitiveFitness.agilityLevel'),
                value: t('traineeReports:na'),
            },
        ],
    };
};

export const buildGoNoGoPlaceholder = (
    t: TFunction,
    item: AssessmentResultItemDataFragment
): CognitiveFitness | null => {
    const mi = item.moduleInstance;
    if (!mi?.moduleDefinitionIdentifier) {
        return null;
    }

    return {
        moduleDefinitionIdentifier: mi.moduleDefinitionIdentifier,
        name: mi.name ?? '',
        description: mi.description ?? '',
        result: [
            {
                label: t('traineeReports:performance.cognitiveFitness.score'),
                value: t('traineeReports:na'),
            },
            {
                label: t('traineeReports:performance.cognitiveFitness.inhibitoryControl'),
                value: t('traineeReports:na'),
            },
            {
                label: t('traineeReports:performance.cognitiveFitness.discriminability'),
                value: t('traineeReports:na'),
            },
            {
                label: t('traineeReports:performance.cognitiveFitness.sustainedAttention'),
                value: t('traineeReports:na'),
            },
            {
                label: t('traineeReports:performance.cognitiveFitness.pressureResilience'),
                value: t('traineeReports:na'),
            },
        ],
    };
};

export const buildConcentrationGridPlaceholder = (
    t: TFunction,
    item: AssessmentResultItemDataFragment
): CognitiveFitness | null => {
    const mi = item.moduleInstance;
    if (!mi?.moduleDefinitionIdentifier) {
        return null;
    }

    return {
        moduleDefinitionIdentifier: mi.moduleDefinitionIdentifier,
        name: mi.name ?? '',
        description: mi.description ?? '',
        result: [
            {
                label: t('traineeReports:performance.cognitiveFitness.completionTime'),
                value: t('traineeReports:na'),
            },
            {
                label: t('traineeReports:performance.cognitiveFitness.completionPercentage'),
                value: t('traineeReports:na'),
            },
            {
                label: t('traineeReports:performance.cognitiveFitness.accuracy'),
                value: t('traineeReports:na'),
            },
            {
                label: t('traineeReports:performance.cognitiveFitness.avgTimePerNumber'),
                value: t('traineeReports:na'),
            },
        ],
    };
};
